import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Lightbulb, Hammer, Building2, HandshakeIcon, Scale, Users, ChevronRight, Calendar, MapPin, Rocket, Clock, Send, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const heroImage = "https://customer-assets.emergentagent.com/job_5ac75a11-ace0-4082-ab6e-a0c86caed3f6/artifacts/zgtxjcx8_d14564f1-09df-4c59-a4ee-80b64efe84c6.JPG";

const foundationPrinciples = [
  {
    icon: Lightbulb,
    title: "Encouraging Idea Creation",
    description: "Innovation begins with conversation. We normalize entrepreneurial thinking as a natural part of campus life."
  },
  {
    icon: Hammer,
    title: "Inspiring Builders, Not Just Thinkers",
    description: "We encourage students to go beyond ideation — to develop tangible products, services, and ventures."
  },
  {
    icon: Building2,
    title: "Long-Term Institutional Platform",
    description: "Unlike short-term clubs, CfE supports entrepreneurship across multiple cohorts of students, alumni, and professors."
  }
];

const catalystPillars = [
  {
    icon: HandshakeIcon,
    title: "Funding Support",
    description: "Seed funding initiatives and investment programs for promising student ventures."
  },
  {
    icon: Scale,
    title: "Legal & Structural Guidance",
    description: "Company registration, legal structuring, compliance, and early-stage governance."
  },
  {
    icon: Users,
    title: "Mentorship & Ecosystem Access",
    description: "Exposure to founders, VCs, and startup operators through events, workshops, and mentorship."
  }
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getLogoUrl = (logoUrl) => {
  if (!logoUrl) return null;
  if (logoUrl.startsWith('cfe-krea/')) return `${API}/files/${logoUrl}`;
  return logoUrl;
};

// Countdown helpers
function getCountdown(targetISO) {
  if (!targetISO) return null;
  const diff = new Date(targetISO).getTime() - Date.now();
  if (diff <= 0) return null;
  return {
    d: Math.floor(diff / (1000 * 60 * 60 * 24)),
    h: Math.floor((diff / (1000 * 60 * 60)) % 24),
    m: Math.floor((diff / (1000 * 60)) % 60),
    s: Math.floor((diff / 1000) % 60)
  };
}
function useCountdown(targetISO) {
  const [cd, setCd] = useState(() => getCountdown(targetISO));
  useEffect(() => {
    if (!targetISO) return;
    const id = setInterval(() => setCd(getCountdown(targetISO)), 1000);
    return () => clearInterval(id);
  }, [targetISO]);
  return cd;
}
function getEventDT(event) {
  if (event.event_datetime) return event.event_datetime;
  if (event.date) return new Date(event.date + 'T00:00:00').toISOString();
  return null;
}
function EventCountdown({ targetISO }) {
  const cd = useCountdown(targetISO);
  if (!cd) return null;
  const parts = [];
  if (cd.d > 0) parts.push(`${cd.d}d`);
  parts.push(`${cd.h}h`, `${cd.m}m`, `${cd.s}s`);
  return (
    <div className="flex items-center gap-1.5 font-mono text-sm font-semibold text-[#C83041]" data-testid="homepage-event-countdown">
      <Clock className="w-3.5 h-3.5" />{parts.join(' ')}
    </div>
  );
}

export default function HomePage() {
  const [events, setEvents] = useState([]);
  const [startups, setStartups] = useState([]);
  const [isIdeaModalOpen, setIsIdeaModalOpen] = useState(false);
  const [ideaSubmitting, setIdeaSubmitting] = useState(false);
  const [ideaForm, setIdeaForm] = useState({ name: '', email: '', phone: '', idea_title: '', description: '' });

  const handleIdeaSubmit = async (e) => {
    e.preventDefault();
    setIdeaSubmitting(true);
    try {
      await axios.post(`${API}/ideas`, ideaForm);
      toast.success('Idea submitted!', { description: 'Thank you for sharing your startup idea with us.' });
      setIdeaForm({ name: '', email: '', phone: '', idea_title: '', description: '' });
      setIsIdeaModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit idea');
    } finally { setIdeaSubmitting(false); }
  };

  useEffect(() => {
    axios.get(`${API}/events`)
      .then(res => {
        const now = Date.now();
        const upcoming = res.data
          .filter(e => {
            const dt = getEventDT(e);
            return dt ? new Date(dt).getTime() > now : !e.is_past;
          })
          .sort((a, b) => new Date(getEventDT(a)) - new Date(getEventDT(b)))
          .slice(0, 3);
        setEvents(upcoming);
      })
      .catch(() => {});
    axios.get(`${API}/startups`)
      .then(res => setStartups(res.data.slice(0, 6)))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative min-h-[90vh] flex items-center pt-20"
        data-testid="hero-section"
      >
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <img 
            src={heroImage} 
            alt="Innovation and collaboration" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-gradient" />
        </div>

        {/* Content */}
        <div className="container-main relative z-10 py-20">
          <div className="max-w-3xl">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium mb-6"
            >
              Krea University × Catalyst
            </motion.span>
            
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 font-['Bricolage_Grotesque'] tracking-tight leading-[0.95]"
              data-testid="hero-heading"
            >
              Where Ideas Become Ventures
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl md:text-2xl text-white/90 mb-10 leading-relaxed font-light"
              data-testid="hero-subheading"
            >
              The Centre for Entrepreneurship at Krea University empowers students, alumni, 
              and faculty to build scalable, sustainable, and impactful startups — supported by Catalyst.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-wrap gap-4"
            >
              <Link to="/startups" data-testid="explore-startups-btn">
                <Button className="bg-white text-[#075FA3] hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1">
                  Explore Our Startups
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/register" data-testid="register-startup-btn">
                <Button className="bg-[#C83041] hover:bg-[#A92332] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1">
                  Register Your Startup
                </Button>
              </Link>
              <Button
                onClick={() => setIsIdeaModalOpen(true)}
                className="bg-white/10 backdrop-blur-sm border-2 border-white/40 text-white hover:bg-white hover:text-[#075FA3] rounded-full px-8 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1"
                data-testid="submit-idea-btn"
              >
                <Lightbulb className="mr-2 w-5 h-5" />
                Submit Your Idea
              </Button>
            </motion.div>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent z-[5] pointer-events-none" />
      </section>

      {/* About CfE Section */}
      <section className="section-padding bg-white" data-testid="about-section">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-[#C83041] font-semibold text-sm tracking-wider uppercase mb-4 block"
            >
              Our Mission
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-bold text-[#231F20] mb-6 font-['Bricolage_Grotesque']"
              data-testid="about-heading"
            >
              Built to Build
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-lg text-[#58595B] leading-relaxed"
            >
              The Centre for Entrepreneurship (CfE) at Krea University was established to cultivate 
              an entrepreneurial culture on campus where ideas are openly discussed, tested, and 
              transformed into real ventures. At its core, CfE is designed to be a centre for 
              building — not merely a student club or society.
            </motion.p>
          </div>

          {/* Foundation Principles */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {foundationPrinciples.map((principle, index) => (
              <motion.div
                key={principle.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full border-0 bg-[#F3F4F6] hover:bg-white hover:shadow-xl transition-all duration-300 group">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 rounded-2xl bg-[#075FA3]/10 flex items-center justify-center mb-6 group-hover:bg-[#075FA3] transition-colors">
                      <principle.icon className="w-7 h-7 text-[#075FA3] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-xl font-semibold text-[#231F20] mb-3 font-['Bricolage_Grotesque']">
                      {principle.title}
                    </h3>
                    <p className="text-[#58595B] leading-relaxed">
                      {principle.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link to="/about" data-testid="learn-more-btn">
              <Button variant="ghost" className="text-[#075FA3] hover:text-[#0077B6] font-semibold group">
                Learn More About Us
                <ChevronRight className="ml-1 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Featured Startups Preview */}
      <section className="section-padding bg-[#F3F4F6]" data-testid="featured-startups-section">
        <div className="container-main">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <motion.span
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="text-[#C83041] font-semibold text-sm tracking-wider uppercase mb-4 block"
              >
                Portfolio
              </motion.span>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-4xl md:text-5xl font-bold text-[#231F20] font-['Bricolage_Grotesque']"
              >
                Startups We've Helped Build
              </motion.h2>
            </div>
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="mt-6 md:mt-0"
            >
              <Link to="/startups" data-testid="view-all-startups-btn">
                <Button className="btn-secondary">
                  View All Startups
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </motion.div>
          </div>

          {startups.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {startups.map((startup, index) => (
                <motion.div
                  key={startup.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to="/startups" state={{ openStartupId: startup.id }} className="block h-full">
                  <Card className="overflow-hidden hover:shadow-lg transition-shadow h-full group cursor-pointer" data-testid={`startup-card-${startup.id}`}>
                    <div className="h-36 bg-gradient-to-br from-[#075FA3]/10 to-[#4DBDDE]/10 flex items-center justify-center p-6">
                      {startup.logo_url ? (
                        <img src={getLogoUrl(startup.logo_url)} alt={startup.name} className="max-h-full max-w-full object-contain" />
                      ) : (
                        <Rocket className="w-12 h-12 text-[#075FA3]/30" />
                      )}
                    </div>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge className="bg-[#075FA3]/10 text-[#075FA3] hover:bg-[#075FA3]/10 text-xs">{startup.sector}</Badge>
                        <Badge variant="outline" className="text-xs">{startup.stage}</Badge>
                      </div>
                      <h3 className="text-lg font-bold text-[#231F20] mb-2 font-['Bricolage_Grotesque'] group-hover:text-[#075FA3] transition-colors">{startup.name}</h3>
                      <p className="text-sm text-[#58595B] line-clamp-2">{startup.tagline}</p>
                    </CardContent>
                  </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-12 text-center border border-dashed border-gray-300"
            >
              <div className="w-20 h-20 rounded-full bg-[#075FA3]/10 flex items-center justify-center mx-auto mb-6">
                <Building2 className="w-10 h-10 text-[#075FA3]" />
              </div>
              <h3 className="text-xl font-semibold text-[#231F20] mb-3 font-['Bricolage_Grotesque']">
                Startups Coming Soon
              </h3>
              <p className="text-[#58595B] max-w-md mx-auto">
                We're currently building our portfolio of innovative startups. 
                Check back soon to see the ventures we're supporting.
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* Events Preview */}
      <section className="section-padding bg-white" data-testid="events-section">
        <div className="container-main">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <motion.span
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="text-[#C83041] font-semibold text-sm tracking-wider uppercase mb-4 block"
              >
                Happenings
              </motion.span>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-4xl md:text-5xl font-bold text-[#231F20] font-['Bricolage_Grotesque']"
              >
                What's Happening at CfE
              </motion.h2>
            </div>
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="mt-6 md:mt-0"
            >
              <Link to="/events" data-testid="see-all-events-btn">
                <Button className="btn-secondary">
                  See All Events
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </motion.div>
          </div>

          {events.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {events.map((event, index) => {
                const dt = getEventDT(event);
                const displayDate = dt
                  ? new Date(dt).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
                  : event.date;
                const displayTime = event.event_datetime
                  ? new Date(event.event_datetime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
                  : event.time;
                return (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to="/events" className="block h-full">
                  <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 h-full group border-0 shadow-md" data-testid={`event-card-${event.id}`}>
                    <div className="h-3 bg-[#C83041]" />
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <Badge className="bg-[#075FA3]/10 text-[#075FA3] hover:bg-[#075FA3]/10">{event.event_type}</Badge>
                        {dt && <EventCountdown targetISO={dt} />}
                      </div>
                      <h3 className="text-xl font-bold text-[#231F20] mb-3 font-['Bricolage_Grotesque'] group-hover:text-[#075FA3] transition-colors">{event.title}</h3>
                      <p className="text-[#58595B] text-sm mb-4">{event.description}</p>
                      <div className="space-y-2 text-sm text-[#58595B]">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-[#C83041]" />
                          <span>{displayDate} at {displayTime}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-[#C83041]" />
                          <span>{event.location}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  </Link>
                </motion.div>
                );
              })}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-[#F3F4F6] rounded-2xl p-12 text-center"
            >
              <div className="w-20 h-20 rounded-full bg-[#C83041]/10 flex items-center justify-center mx-auto mb-6">
                <Users className="w-10 h-10 text-[#C83041]" />
              </div>
              <h3 className="text-xl font-semibold text-[#231F20] mb-3 font-['Bricolage_Grotesque']">
                Events Coming Soon
              </h3>
              <p className="text-[#58595B] max-w-md mx-auto">
                Stay tuned for upcoming hackathons, workshops, pitch nights, and more. 
                We're planning exciting events for our community.
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* Advisor Booking CTA Banner */}
      <section 
        className="py-20 bg-[#075FA3] relative overflow-hidden"
        data-testid="advisor-cta-section"
      >
        <div className="absolute inset-0 subtle-gradient opacity-50" />
        <div className="container-main relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-bold text-white mb-6 font-['Bricolage_Grotesque']"
            >
              Need Guidance for Your Startup?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-xl text-white/90 mb-10 leading-relaxed"
            >
              Book a one-on-one session with experienced advisors from our network of 
              founders, investors, and startup operators.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Link to="/advisors" data-testid="book-advisor-btn">
                <Button className="bg-white text-[#075FA3] hover:bg-gray-100 rounded-full px-10 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1">
                  Book an Advisor Meeting
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Catalyst Partnership Section */}
      <section className="section-padding bg-white" data-testid="catalyst-section">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-bold text-[#231F20] mb-6 font-['Bricolage_Grotesque']"
            >
              Built at Catalyst
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-lg text-[#58595B] leading-relaxed"
            >
              A key institutional partner supporting CfE is Catalyst Atal Incubation Center — 
              an impact-focused startup accelerator originally established by IFMR under the 
              Atal Innovation Mission of NITI Aayog. Catalyst supports startups developing 
              scalable and inclusive solutions across fintech, sustainability, agriculture, 
              craft industries, and inclusive technology.
            </motion.p>
          </div>

          {/* Support Pillars */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {catalystPillars.map((pillar, index) => (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full border border-gray-200 hover:border-[#4DBDDE]/50 hover:shadow-xl transition-all duration-300 group">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 rounded-2xl bg-[#4DBDDE]/10 flex items-center justify-center mb-6 group-hover:bg-[#4DBDDE] transition-colors">
                      <pillar.icon className="w-7 h-7 text-[#0077B6] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-xl font-semibold text-[#231F20] mb-3 font-['Bricolage_Grotesque']">
                      {pillar.title}
                    </h3>
                    <p className="text-[#58595B] leading-relaxed">
                      {pillar.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[#C83041]" data-testid="final-cta-section">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-bold text-white mb-6 font-['Bricolage_Grotesque']"
            >
              Ready to Build Something Amazing?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-xl text-white/90 mb-10"
            >
              Join the CfE ecosystem and take the first step towards launching your venture.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Link to="/register" data-testid="final-cta-register-btn">
                <Button className="bg-white text-[#C83041] hover:bg-gray-100 rounded-full px-10 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1">
                  Register Your Startup Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Submit Idea Modal */}
      {isIdeaModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setIsIdeaModalOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in slide-in-from-bottom-4 duration-300" data-testid="idea-modal">
            <div className="bg-gradient-to-r from-[#075FA3] to-[#4DBDDE] px-6 py-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                    <Lightbulb className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white font-['Bricolage_Grotesque']">Submit Your Idea</h3>
                    <p className="text-white/70 text-sm">Share your startup vision with us</p>
                  </div>
                </div>
                <button onClick={() => setIsIdeaModalOpen(false)} className="text-white/80 hover:text-white p-1" data-testid="idea-close-btn">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <form onSubmit={handleIdeaSubmit} className="p-6 space-y-4">
              <div>
                <Label className="text-sm font-medium text-[#231F20]">Name *</Label>
                <Input value={ideaForm.name} onChange={(e) => setIdeaForm({ ...ideaForm, name: e.target.value })} required placeholder="Your full name" className="mt-1" data-testid="idea-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-medium text-[#231F20]">Email *</Label>
                  <Input type="email" value={ideaForm.email} onChange={(e) => setIdeaForm({ ...ideaForm, email: e.target.value })} required placeholder="you@email.com" className="mt-1" data-testid="idea-email-input" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#231F20]">Phone *</Label>
                  <Input type="tel" value={ideaForm.phone} onChange={(e) => setIdeaForm({ ...ideaForm, phone: e.target.value })} required placeholder="+91 XXXXX" className="mt-1" data-testid="idea-phone-input" />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium text-[#231F20]">Startup Idea Title *</Label>
                <Input value={ideaForm.idea_title} onChange={(e) => setIdeaForm({ ...ideaForm, idea_title: e.target.value })} required placeholder="Give your idea a name" className="mt-1" data-testid="idea-title-input" />
              </div>
              <div>
                <Label className="text-sm font-medium text-[#231F20]">Description *</Label>
                <Textarea value={ideaForm.description} onChange={(e) => setIdeaForm({ ...ideaForm, description: e.target.value })} required placeholder="Describe your startup idea, the problem it solves, and your vision..." className="mt-1 min-h-[120px]" data-testid="idea-desc-input" />
              </div>
              <Button type="submit" disabled={ideaSubmitting || !ideaForm.name || !ideaForm.email || !ideaForm.phone || !ideaForm.idea_title || !ideaForm.description}
                className="w-full bg-[#C83041] hover:bg-[#A92332] text-white rounded-full py-6 font-semibold" data-testid="idea-submit-btn">
                {ideaSubmitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : <><Send className="w-4 h-4 mr-2" /> Submit Your Idea</>}
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
